/**
 * Hook d'authentification pour BTPal
 * Gère l'état utilisateur et les actions auth
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi, setToken, setRefreshToken, clearTokens, getToken } from '@/services/api';
import type { User } from '@/types';

// ========================================
// Types
// ========================================

interface TwoFactorPending {
    pendingToken: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    twoFactorPending: TwoFactorPending | null;
    login: (email: string, password: string) => Promise<void>;
    validate2FA: (code: string) => Promise<void>;
    cancel2FA: () => void;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

// ========================================
// Context
// ========================================

const AuthContext = createContext<AuthContextType | null>(null);

// ========================================
// Provider
// ========================================

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [twoFactorPending, setTwoFactorPending] = useState<TwoFactorPending | null>(null);

    /**
     * Vérifie si l'utilisateur est déjà connecté au montage
     */
    useEffect(() => {
        const checkAuth = async () => {
            const token = getToken();

            if (!token) {
                console.log('[Auth] Pas de token, utilisateur non connecté');
                setIsLoading(false);
                return;
            }

            try {
                console.log('[Auth] Vérification du token...');
                const userData = await authApi.getMe();
                console.log('[Auth] Utilisateur authentifié:', userData.email);
                setUser(userData);
            } catch (error) {
                console.error('[Auth] Token invalide, nettoyage:', error);
                clearTokens();
                setUser(null);
                // Note: pas de redirection ici, le ProtectedRoute s'en charge
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, []);

    /**
     * Connexion utilisateur
     */
    const login = useCallback(async (email: string, password: string): Promise<void> => {
        const response = await authApi.login(email, password);

        // 2FA required — store pending token and wait for code
        if (response.requires2FA && response.pendingToken) {
            setTwoFactorPending({ pendingToken: response.pendingToken });
            return;
        }

        // No 2FA — proceed normally
        setToken(response.token!);
        setRefreshToken(response.refreshToken!);
        setUser(response.user!);
    }, []);

    const validate2FA = useCallback(async (code: string): Promise<void> => {
        if (!twoFactorPending) throw new Error('Aucune authentification 2FA en attente');

        const response = await authApi.validate2FA(twoFactorPending.pendingToken, code);

        setToken(response.token!);
        setRefreshToken(response.refreshToken!);
        setUser(response.user!);
        setTwoFactorPending(null);
    }, [twoFactorPending]);

    const cancel2FA = useCallback(() => {
        setTwoFactorPending(null);
    }, []);

    /**
     * Déconnexion utilisateur
     */
    const logout = useCallback(async (): Promise<void> => {
        try {
            await authApi.logout();
        } catch (error) {
            console.error('[Auth] Erreur lors de la déconnexion:', error);
        } finally {
            setUser(null);
        }
    }, []);

    /**
     * Rafraîchir les données utilisateur
     */
    const refreshUser = useCallback(async (): Promise<void> => {
        try {
            const userData = await authApi.getMe();
            setUser(userData);
        } catch (error) {
            console.error('[Auth] Erreur lors du rafraîchissement:', error);
            throw error;
        }
    }, []);

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        twoFactorPending,
        login,
        validate2FA,
        cancel2FA,
        logout,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// ========================================
// Hook
// ========================================

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}

export default useAuth;
